import axios from "axios";
import {Engine} from '@sellers-shield-home-protection/ss-core-js';
import {AppResourceSearch, ApiFilter} from '@sellers-shield-home-protection/ss-core-js';
import {useAuthStore} from '@store';
import {vuexSspToken} from '@store';
import FrontEndApiService from "@services/frontend.api.service";
import {AuthService} from '@services/auth.service';
import {isOnMainDomain, matchFirstUuidInUrl} from "@helpers/router-helper";
import {refreshUserData} from "@helpers/user";
import {debugf} from "@helpers/string";

export const INTEGRATIONS = {
    zipform: Boolean(window.env?.ZIPFORM_IFRAME_BASE_URL && window.env?.ZIPFORM_SHARED_KEY),
    dotloop: Boolean(window.env?.DOTLOOP_IFRAME_BASE_URL && window.env?.DOTLOOP_EXTERNAL_ID),
};


/*
*
* when setupEngine setAsPublic = true, the API_PREFIX is "public-api/v1/" which loads "productClient" as the middleware
*    this productClient middleware binds to CheckProductCredentials, and validates the incoming request's token->client->skipsAuthorization()
*    skipsAuthorization() is our definition, and only returns true for LuClientType::PRODUCT
*
* when setupEngine setAsPublic = false, the API_PREFIX is "api/v1/" which loads "auth:api" as the middleware
*    this auth:api middleware uses the defined api guard; which we have set to use passport
*    https://laravel.com/docs/7.x/authentication#protecting-routes
*
*/
export const setupEngine = (env = null, setAsPublic = false) => {
    env = env || window.env;

    let origin = window.location.origin,
        accepted = [env?.APP_URL, env?.APP_AGENT_URL, env?.APP_SELLER_URL, env?.APP_BROKER_URL, env?.APP_TC_URL].filter(e => !!e),
        appUrl = accepted.indexOf(origin) > -1 ? origin : env.APP_URL;

    let apiHost = setAsPublic ? appUrl : env.MILESTONES_API_HOST;
    let apiPrefix = setAsPublic ? env.MILESTONES_PUBLIC_API_PREFIX : env.MILESTONES_API_PREFIX;
    let apiVersion = env.MILESTONES_API_VERSION;

    Engine.config({
        xhrLib: axios,
        debug: true,
        apiHost: apiHost,
        apiPrefix: apiPrefix,
        apiVersion: apiVersion,
    });

    FrontEndApiService.init(appUrl);
    return {apiHost, apiPrefix, apiVersion};
};

export const setupAxiosInterceptors = (accessToken = null) => {
    if (Boolean(accessToken)) {
        FrontEndApiService.mountRequestAuthInterceptor(accessToken);
    } else {
        FrontEndApiService.mountRequestInterceptor();
    }
    FrontEndApiService.mount401Interceptor();
};

export const setupEngineAndAxiosInterceptors = (accessToken = null, env = null) => {
    setupEngine(env);
    setupAxiosInterceptors(accessToken);
};

export async function runAsPublic(callback, resetConfig = true) {
    let response;
    try {
        let config = setupEngine(null, true);
        response = await callback(config);
    } catch (error) {
        console.log(error);
    } finally {
        resetConfig && setupEngine();
    }
    return response;
}

export async function fetchReferringPlatforms(params, runPublic = true, resetConfig = true, pageSize = 1) {
    if (params) {
        const appResourceSearch = new AppResourceSearch('referring-platform');

        await appResourceSearch.setSearchData({
            pageSize: pageSize,
            filter: new ApiFilter({
                params: params,
            }),
        }, false);

        await runAsPublic(async () => {
            await appResourceSearch.search();
        }, resetConfig);

        return appResourceSearch.appResourceCollection.toArray() || [];
    }

    return [];
}

export const getActiveThirdPartyPlatformList = async (force = false, runPublic = false) => {
    const storeAuth = useAuthStore();

    if (storeAuth._thirdPartyPlatformList === null || force && (storeAuth._thirdPartyPlatformList || []).length === 0) {
        await storeAuth.getThirdPartyPlatformList(runPublic || !Boolean(storeAuth.me?.uuid));
    }

    return storeAuth._thirdPartyPlatformList || [];
};

export const availableThirdPartyPlatformList = async (testAgainst = null, force = false) => {
    let records = await getActiveThirdPartyPlatformList(force);
    return Object.keys(INTEGRATIONS).filter(
        platform => {
            let accepted = [platform, platform === 'zipform' ? 'lonewolf' : platform];
            return platform && INTEGRATIONS[platform] && (
                testAgainst === null || testAgainst && accepted.includes(testAgainst) && Boolean(records.find(p => accepted.includes(p?.slug)))
            );
        }
    );
};

export const getThirdPartyPlatformRedirectPage = ($key) => {
    return {
        inviteSeller: 'invite-seller',
        orderDetails: 'order-details',
        'invite-seller': 'invite-seller',
        'order-details': 'order-details',
    }[$key] || null;
};

export const checkLoggedSession = async (forceLogout = false, forceRefreshUser = true) => {
    console.log('api-helper.js checkLoggedSession() entry', {a: forceLogout, b: forceRefreshUser });
    const storeAuth = useAuthStore();

    let isGuest = isOnMainDomain(),
        {matchUuid, urlToken} = matchFirstUuidInUrl(
            isGuest ? {pathname: '/'} : window.location
        ),
        loggedIn = Boolean(storeAuth.accessToken),
        uuid = loggedIn ? (storeAuth.me?.uuid || storeAuth.me?._uuid) : matchUuid;

    if (matchUuid && !isGuest && uuid !== matchUuid && vuexSspToken && window.location.href.indexOf('/dashboard') > -1) {
        await storeAuth.logout();
        loggedIn = false;
        uuid = matchUuid;
    }

    if (!forceLogout) {
        try {
            if (loggedIn || vuexSspToken) {
                if (uuid) {
                    if (!loggedIn && !isGuest) {
                        //only try this on secondary subdomains
                        setupEngine();
                        const {accessToken, refreshToken} = await AuthService.loginBySspToken(uuid);
                        await storeAuth.setTokensGetMe(accessToken, refreshToken, false);
                    }
                    //directly set attribute - don't blame me
                    storeAuth._me.uuid = uuid;
                }

                loggedIn = Boolean(storeAuth.accessToken) && Boolean(storeAuth.me?.uuid);

                if (loggedIn) {
                    setupEngineAndAxiosInterceptors(storeAuth.accessToken);
                    console.log('api-helper.js checkLoggedSession() calling refreshUserData', {a: uuid, b: forceRefreshUser || !storeAuth.me?.exists, c: true});
                    await refreshUserData(uuid, forceRefreshUser || !storeAuth.me?.exists, true);
                }
            }
        } catch (e) {
            loggedIn = false;
            console.log(e)
        }

        if ((!loggedIn || !storeAuth.me?.exists) && (Boolean(storeAuth.accessToken) || Boolean(storeAuth.me?.uuid))) {
            forceLogout = loggedIn = true;
        }
    }

    // debugf(['5.checkIf--logged user session is valid'], {loggedIn, forceLogout}, {
    //     accessToken: storeAuth.accessToken,
    //     refreshToken: storeAuth.refreshToken,
    //     me: storeAuth.me,
    // });

    if (forceLogout && loggedIn) {
        await storeAuth.logout();
    }

    if (uuid && urlToken && [matchUuid, urlToken].includes(uuid)) {
        window.history.replaceState(
            window.location.origin,
            null,
            window.location.pathname.replace(`/${urlToken}`, '') + window.location.search
        );
    }
}

export const revalidateAgentLicensesAfterLogin = async() => {
    console.log('api-helper.js - revalidateAgentLicenses');
    const storeAuth = useAuthStore();
    
    await storeAuth.revalidateAndStoreTxLicense();
};